/* PostHog for the Fackelmann storefront: loads only with analytics consent and sends page and feature events.
 * Configured by snippets/fk-analytics.liquid. Event list, privacy rules and verification: docs/posthog.md.
 * Features report events with document.dispatchEvent(new CustomEvent('fk:track', {detail: {event, properties}}))
 * or declaratively with data-fk-track="event_name" data-fk-track-<property>="value" on a clickable element.
 */
(() => {
  const element = document.getElementById('fk-analytics-config');
  if (!element || window.fkAnalytics) return;
  let config;
  try {
    config = JSON.parse(element.textContent);
  } catch (error) {
    return;
  }
  const token = String(config.token || '').trim();
  if (!/^phc_[A-Za-z0-9]+$/.test(token)) return;

  const QUEUE_LIMIT = 50;
  const PRIVATE_KEY = /e-?mail|phone|password|token|address|post_?code|postal|zip|first_?name|last_?name|full_?name|customer_?id/i;
  const EMAIL = /[^\s@/?&=]+@[^\s@/?&=]+\.[a-z]{2,}/gi;
  const PRIVATE_SELECTOR = '[data-fk-private]';
  let state = 'pending';
  let started = false;
  let ready = false;
  let client;
  let queue = [];
  const CONTENT_CONTEXT_KEY = 'fk_content_context';
  const contentSeen = new Set();
  let contentObserver;

  function contentContext(properties) {
    if (state !== 'granted' || !ready) return {};
    try {
      const context = JSON.parse(sessionStorage.getItem(CONTENT_CONTEXT_KEY) || 'null');
      if (!context) return {};
      if (context.expires_at < Date.now() || context.session_id !== client.get_session_id()) {
        sessionStorage.removeItem(CONTENT_CONTEXT_KEY);
        return {};
      }
      const targetId = context.target_type === 'product' ? properties.product_id : properties.collection_id;
      const expectedId = context.target_type === 'product' ? context.product_id : context.collection_id;
      if (String(targetId || '') !== String(expectedId || '')) return {};
      const { expires_at, session_id, ...attribution } = context;
      return { content_attribution: attribution };
    } catch (error) { return {}; }
  }

  function rememberContent(properties, expiresAt) {
    if (state !== 'granted' || !ready) return;
    const session = client.get_session_id();
    if (typeof session !== 'string' || !session) return;
    try {
      sessionStorage.setItem(CONTENT_CONTEXT_KEY, JSON.stringify(Object.assign({}, properties, {
        session_id: session, expires_at: Math.min(expiresAt || Infinity, Date.now() + 30 * 60 * 1000),
      })));
    } catch (error) { /* Storage is optional. */ }
  }

  function followCollectionProduct(target) {
    if (state !== 'granted' || !ready || config.page?.event !== 'collection_viewed') return;
    const card = target.closest('.product-grid__item[data-product-id]');
    if (!card) return;
    const clickedLink = target.closest('a[href]');
    if (!clickedLink && !target.closest('fk-add-button')) return;
    const link = clickedLink || card.querySelector('a[href*="/products/"]');
    const productId = toId(card.dataset.productId);
    if (!link || !productId) return;
    try {
      const context = JSON.parse(sessionStorage.getItem(CONTENT_CONTEXT_KEY) || 'null');
      const collectionId = context?.entry_collection_id || (context?.target_type === 'collection' && context.collection_id);
      if (!context || !collectionId || String(collectionId) !== String(config.page.properties.collection_id)) return;
      if (context.expires_at < Date.now() || context.session_id !== client.get_session_id()) return;
      const url = new URL(link.href, location.href);
      if (url.host !== location.host || !/\/products\/[^/]+\/?$/.test(url.pathname)) return;
      const { expires_at, session_id, ...source } = context;
      rememberContent(Object.assign({}, source, {
        entry_collection_id: String(collectionId),
        entry_target_path: context.entry_target_path || context.target_path,
        target_type: 'product', target_path: url.pathname, product_id: productId,
      }), expires_at);
    } catch (error) { /* Attribution must never interrupt a shopping action. */ }
  }

  function observeContent() {
    if (state !== 'granted' || !ready || !('IntersectionObserver' in window)) return;
    if (!contentObserver) contentObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.25 || state !== 'granted') return;
        const properties = dataProperties(entry.target);
        const key = properties.source_path + ':' + properties.module_id;
        if (contentSeen.has(key)) return;
        contentSeen.add(key);
        track('content_commerce_impression', properties);
        contentObserver.unobserve(entry.target);
      });
    }, { threshold: 0.25 });
    document.querySelectorAll('[data-fk-commerce-module]').forEach((module) => contentObserver.observe(module));
  }

  function clean(value, depth = 0) {
    if (Array.isArray(value)) return value.slice(0, 50).map((entry) => clean(entry, depth + 1));
    if (value && typeof value === 'object') {
      if (depth > 3) return undefined;
      const result = {};
      for (const [key, entry] of Object.entries(value)) {
        if (PRIVATE_KEY.test(key)) continue;
        const cleaned = clean(entry, depth + 1);
        if (cleaned !== undefined) result[key] = cleaned;
      }
      return result;
    }
    if (typeof value === 'string') return value.replace(EMAIL, '[email]').slice(0, 500);
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    return typeof value === 'boolean' || value === null ? value : undefined;
  }

  function capture(event, properties) {
    (client || window.posthog).capture(event, properties);
  }

  function track(event, properties) {
    if (typeof event !== 'string' || !/^[a-z][a-z0-9_]{1,63}$/.test(event) || state === 'denied') return;
    const payload = clean(properties || {}) || {};
    if (['product_viewed', 'collection_viewed', 'product_added_to_cart'].includes(event)) Object.assign(payload, contentContext(payload));
    if (state === 'granted' && ready) capture(event, payload);
    else if (queue.length < QUEUE_LIMIT) queue.push([event, payload]);
  }

  function superProperties() {
    const theme = (window.Shopify && window.Shopify.theme) || {};
    return Object.assign({}, clean(config.context || {}), { theme_id: theme.id, theme_role: theme.role });
  }

  function redactUrls(event) {
    if (!event || !event.properties) return event;
    for (const key of ['$current_url', '$pathname', '$referrer', '$el_text', '$elements_chain']) {
      if (typeof event.properties[key] === 'string') event.properties[key] = event.properties[key].replace(EMAIL, '[email]');
    }
    return event;
  }

  // Shopify renders 404 pages at /404, so the requested path and the linking page come from the browser.
  function pageProperties() {
    const properties = Object.assign({}, config.page.properties);
    if (config.page.event === 'page_not_found') {
      properties.path = location.pathname;
      try {
        const referrer = document.referrer ? new URL(document.referrer) : null;
        if (referrer) properties[referrer.host === location.host ? 'referrer_path' : 'referrer_host'] = referrer.host === location.host ? referrer.pathname : referrer.host;
      } catch (error) {
        /* no usable referrer */
      }
    }
    return properties;
  }

  function onLoaded(instance) {
    client = instance;
    if (state !== 'granted') {
      instance.stopSessionRecording();
      instance.opt_out_capturing();
      forgetStorage();
      return;
    }
    // Persistence is disabled by default even when capture itself is not opted out.
    // Loading only follows explicit consent; opt in here to preserve that visitor across pages.
    instance.opt_in_capturing({ captureEventName: false });
    instance.register(superProperties());
    ready = true;
    observeContent();
    if (config.page && config.page.event) track(config.page.event, pageProperties());
    const pending = queue;
    queue = [];
    pending.forEach(([event, properties]) => capture(event, properties));
  }

  // Official PostHog web snippet (posthog.com/docs/libraries/js): a call-queue stub that loads array.js.
  function stub() {
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],Object.defineProperty(u,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}),Object.defineProperty(u.people,"toString",{configurable:!0,enumerable:!0,writable:!0,value:function(){return u.toString(1)+".people (stub)"}}),o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagResult isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  }

  function start() {
    started = true;
    stub();
    window.posthog.init(token, {
      api_host: config.apiHost || 'https://eu.i.posthog.com',
      ui_host: config.uiHost || 'https://eu.posthog.com',
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      opt_out_persistence_by_default: true,
      capture_pageleave: true,
      capture_dead_clicks: true,
      capture_heatmaps: config.heatmaps === true,
      capture_exceptions: true,
      capture_performance: { web_vitals: true, network_timing: true },
      disable_session_recording: config.replay !== true,
      enable_recording_console_log: false,
      session_recording: { maskAllInputs: true, maskTextSelector: PRIVATE_SELECTOR },
      mask_personal_data_properties: true,
      custom_personal_data_properties: ['email', 'key', 'token', 'checkout_token', 'phone'],
      autocapture: { css_selector_ignorelist: ['.ph-no-autocapture', '[data-ph-no-autocapture]', PRIVATE_SELECTOR] },
      before_send: redactUrls,
      loaded: onLoaded,
    });
  }

  // Without consent no PostHog identifier may stay on the device, including one from an earlier visit.
  // Once PostHog runs on this page its opt-out marker stays: it is what keeps the loaded instance silent.
  function forgetStorage() {
    const prefix = 'ph_' + token;
    const optOutKey = '__ph_opt_in_out_' + token;
    const owned = (name) => name === CONTENT_CONTEXT_KEY || name.startsWith(prefix) || (!started && name === optOutKey);
    if (contentObserver) contentObserver.disconnect();
    contentSeen.clear();
    for (const store of ['localStorage', 'sessionStorage']) {
      try {
        Object.keys(window[store]).filter(owned).forEach((key) => window[store].removeItem(key));
      } catch (error) {
        /* storage unavailable */
      }
    }
    const parts = location.hostname.split('.');
    const domains = [''];
    for (let i = 0; i < parts.length - 1; i += 1) domains.push('; domain=.' + parts.slice(i).join('.'));
    document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter(owned)
      .forEach((name) => {
        domains.forEach((domain) => {
          document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' + domain;
        });
      });
  }

  function grant() {
    if (state === 'granted') return;
    state = 'granted';
    if (started) {
      // A consent change can happen while array.js is still loading. Its loaded
      // callback must make the decision, rather than replaying a stale queued opt-in.
      if (!client) return;
      if (!ready) { onLoaded(client); return; }
      client.opt_in_capturing({ captureEventName: false });
      observeContent();
    }
    else start();
  }

  function deny(decided) {
    if (decided) {
      state = 'denied';
      queue = [];
    }
    if (client) {
      client.stopSessionRecording();
      client.opt_out_capturing();
    }
    forgetStorage();
  }

  function apply(allowed, decided) {
    if (allowed) grant();
    else deny(decided || state === 'granted');
  }

  function evaluate() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return;
    let allowed = false;
    let decision = '';
    try {
      allowed = privacy.analyticsProcessingAllowed() === true;
      decision = (privacy.currentVisitorConsent() || {}).analytics || '';
      allowed = allowed && decision === 'yes';
    } catch (error) {
      /* treat as not allowed */
    }
    apply(allowed, decision === 'no');
  }

  // Typing states (search fields) are reported once they settle for 1.5 s, not per keystroke.
  const settling = new Map();
  function settle(key, event, properties) {
    const pending = settling.get(key);
    if (pending) clearTimeout(pending.timer);
    settling.set(key, { event, properties, timer: setTimeout(() => flush(key), 1500) });
  }
  function flush(key) {
    for (const [name, entry] of settling) {
      if (key && name !== key) continue;
      clearTimeout(entry.timer);
      settling.delete(name);
      track(entry.event, entry.properties);
    }
  }

  const toId = (value) => {
    const id = String(value === undefined || value === null ? '' : value).split('/').pop();
    return /^\d+$/.test(id) ? Number(id) : id || undefined;
  };
  const amount = (value) => (value === undefined || value === null || value === '' ? undefined : Number(value));
  const cart = { value: amount(config.cart && config.cart.value), items: config.cart && config.cart.items_count };

  function summarize(summary) {
    if (!summary) return {};
    const total = summary.cost && summary.cost.totalAmount;
    return { cart_value: amount(total && total.amount), cart_items_count: summary.totalQuantity, currency: total && total.currencyCode };
  }

  // Remembers the basket total and reports when it first passes the free-delivery threshold.
  function updateCart(summary) {
    if (!summary || summary.cart_value === undefined) return;
    const threshold = Number(config.freeShippingThreshold) || 0;
    if (threshold > 0 && cart.value !== undefined && cart.value < threshold && summary.cart_value >= threshold) {
      track('free_shipping_threshold_reached', { cart_value: summary.cart_value, threshold });
    }
    cart.value = summary.cart_value;
    cart.items = summary.cart_items_count;
  }

  function addSource(target) {
    if (!(target instanceof Element)) return 'other';
    if (target.closest('fk-cart-upsell')) return 'cart_upsell';
    if (target.closest('.fk-look__dialog')) return 'shop_the_look';
    if (target.closest('fk-wishlist-drawer')) return 'wishlist';
    if (target.closest('fk-cart-empty')) return 'empty_cart';
    if (target.closest('quick-add-dialog')) return 'quick_add';
    if (target.closest('fk-add-button')) return 'product_card';
    if (target.closest('product-form-component')) return target.querySelector('[data-puppet="true"]') ? 'sticky_bar' : 'product_page';
    return 'other';
  }

  function cartRow(root, key) {
    const row = root instanceof Element ? [...root.querySelectorAll('[data-key]')].find((entry) => entry.dataset.key === key) : null;
    const text = (selector) => {
      const node = row && row.querySelector(selector);
      return (node && node.textContent.trim()) || undefined;
    };
    const input = row && row.querySelector('input[type="number"]');
    return {
      variant_id: toId(String(key || '').split(':')[0]),
      product_title: text('.cart-items__title'),
      brand: text('.cart-items__vendor'),
      previous_quantity: input ? amount(input.defaultValue) : undefined,
    };
  }

  function describeFilters(filters) {
    return (filters || []).slice(0, 20).map((filter) => {
      if (filter.productVendor) return 'brand:' + filter.productVendor;
      if (filter.productType) return 'type:' + filter.productType;
      if (filter.variantOption) return 'option:' + filter.variantOption.name + '=' + (filter.variantOption.value || '');
      if (filter.price) return 'price:' + (filter.price.min === undefined ? '' : filter.price.min) + '-' + (filter.price.max === undefined ? '' : filter.price.max);
      if (filter.available !== undefined) return 'available:' + filter.available;
      if (filter.tag) return 'tag:' + filter.tag;
      if (filter.category) return 'category:' + toId(filter.category.id);
      const field = filter.productMetafield || filter.variantMetafield || filter.taxonomyMetafield;
      return field ? 'metafield:' + (field.namespace ? field.namespace + '.' : '') + field.key + '=' + (field.value || '') : 'other';
    });
  }

  function dataProperties(element) {
    const properties = {};
    for (const [key, value] of Object.entries(element.dataset)) {
      if (key.length > 7 && key.startsWith('fkTrack')) {
        properties[key.slice(7).replace(/[A-Z]/g, (letter, index) => (index ? '_' : '') + letter.toLowerCase())] = value;
      }
    }
    return properties;
  }

  function resultType(href) {
    const path = new URL(href, location.href).pathname;
    if (/\/products\//.test(path)) return 'product';
    if (/\/collections\//.test(path)) return 'collection';
    if (/\/blogs\//.test(path)) return 'recipe';
    if (/\/pages\//.test(path)) return 'page';
    return /\/search/.test(path) ? 'all_results' : 'other';
  }

  window.fkAnalytics = { track };
  document.addEventListener('fk:track', (event) => {
    const detail = event.detail || {};
    if (detail.settle) settle(detail.settle, detail.event, detail.properties);
    else {
      flush();
      track(detail.event, detail.properties);
    }
  });
  addEventListener('pagehide', () => flush());

  // Horizon reports cart, search, filter and variant changes as Shopify storefront events with a result promise.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const lines = event.lines || [];
    const root = event.target;
    const surface = root instanceof Element && root.closest('#cart-drawer') ? 'drawer' : 'page';
    const source = event.action === 'add' ? addSource(root) : undefined;
    const rows = event.action === 'add' ? [] : lines.map((line) => cartRow(root, line.id));
    if (!event.promise) return;
    event.promise
      .then((result) => {
        const detail = (result && result.detail) || {};
        if (detail.didError) return;
        const summary = summarize(result && result.cart);
        if (detail.source !== 'fk-bundle-steps' && detail.source !== 'fk-rewards') {
          lines.forEach((line, index) => {
            if (event.action === 'add') {
              const variantId = toId(line.merchandiseId);
              const item = (detail.items || []).find((entry) => toId(entry.variant_id || entry.id) === variantId) || {};
              const price = item.price === undefined ? undefined : Number(item.price) / 100;
              track('product_added_to_cart', Object.assign({
                source, product_id: item.product_id, variant_id: variantId, product_title: item.product_title,
                brand: item.vendor, product_type: item.product_type, sku: item.sku, price, quantity: line.quantity,
                value: price === undefined ? undefined : Math.round(price * line.quantity * 100) / 100,
              }, summary));
            } else {
              track(line.quantity > 0 ? 'cart_quantity_changed' : 'product_removed_from_cart', Object.assign({ surface, quantity: line.quantity }, rows[index], summary));
            }
          });
        }
        updateCart(summary);
      })
      .catch(() => {});
  });
  document.addEventListener('shopify:cart:error', (event) => {
    track('cart_error', { code: event.code, message: String(event.error || '').slice(0, 160) });
  });
  document.addEventListener('shopify:cart:view', (event) => {
    const summary = summarize(event.cart);
    track('cart_viewed', Object.assign({ surface: event.context === 'dialog' ? 'drawer' : 'page' }, summary));
    updateCart(summary);
  });
  document.addEventListener('shopify:product:view', (event) => {
    if (event.context !== 'dialog' || !event.product) return;
    const variant = event.product.selectedVariant || {};
    track('quick_view_opened', {
      product_id: toId(event.product.id), product_handle: event.product.handle, product_title: event.product.title,
      variant_id: toId(variant.id), price: amount(variant.price && variant.price.amount), available: variant.availableForSale,
    });
  });
  document.addEventListener('shopify:product:select', (event) => {
    if (!event.product || !event.promise) return;
    const surface = event.target instanceof Element && event.target.closest('quick-add-dialog') ? 'quick_add' : 'product_page';
    const options = (event.selectedOptions || []).map((option) => option.name + ': ' + option.value);
    event.promise
      .then((result) => {
        const variant = (result && result.variant) || null;
        track('variant_selected', {
          surface, product_id: toId(event.product.id), product_handle: event.product.handle, options,
          variant_id: variant ? toId(variant.id) : null, available: variant ? variant.availableForSale : false,
          price: variant ? amount(variant.price && variant.price.amount) : undefined,
        });
      })
      .catch(() => {});
  });
  document.addEventListener('shopify:collection:update', (event) => {
    if (!event.promise) return;
    const filters = describeFilters(event.productFilters);
    event.promise
      .then((result) => {
        track('collection_filtered', {
          collection_handle: event.collection && event.collection.handle, filters, filters_count: filters.length,
          sort_by: event.sortKey, results_count: result && result.productsCount,
        });
      })
      .catch(() => {});
  });
  document.addEventListener('shopify:search:update', (event) => {
    if (!event.promise || !event.search) return;
    const predictive = event.target instanceof Element && event.target.closest('predictive-search-component');
    const query = String(event.search.query || '').trim();
    const filters = describeFilters(event.search.productFilters);
    event.promise
      .then((result) => {
        const count = result && result.totalCount;
        if (!predictive) {
          track('search_filtered', { search_term: query, filters, filters_count: filters.length, sort_by: event.search.sortKey, results_count: count });
        } else if (query.length >= 2) {
          settle('search:modal', 'search_suggestions_viewed', { surface: 'modal', search_term: query, results_count: count });
        }
      })
      .catch(() => {});
  });
  document.addEventListener('theme-drawer:open', (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'fk-wishlist-drawer') return;
    const items = window.fkWishlist && typeof window.fkWishlist.read === 'function' ? window.fkWishlist.read() : [];
    track('wishlist_viewed', { items_count: items.length });
  });

  // Bubbling phase: components that stop a click (for example after a drag) are not counted.
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    followCollectionProduct(target);
    const declared = target.closest('[data-fk-track]');
    if (declared) {
      flush();
      const module = declared.closest('[data-fk-commerce-module]');
      const properties = Object.assign({}, module ? dataProperties(module) : {}, dataProperties(declared));
      if (declared.dataset.fkTrack === 'content_commerce_clicked') {
        if (state !== 'granted' || !ready) return;
        rememberContent(properties);
      }
      track(declared.dataset.fkTrack, properties);
    }
    const result = target.closest('predictive-search-component a[href]');
    if (result) {
      const component = result.closest('predictive-search-component');
      const input = component.querySelector('input[type="search"], input');
      const links = [...component.querySelectorAll('a[href]')];
      flush();
      track('search_result_clicked', {
        surface: 'modal', search_term: input ? input.value.trim() : undefined,
        result_type: resultType(result.href), position: links.indexOf(result) + 1,
      });
    }
    const checkout = target.closest('button[name="checkout"]');
    if (checkout) {
      track('checkout_button_clicked', { surface: checkout.closest('#cart-drawer') ? 'drawer' : 'page', cart_value: cart.value, cart_items_count: cart.items });
    }
    if (target.closest('.accelerated-checkout-block')) track('express_checkout_clicked', { surface: 'product_page' });
  });
  document.addEventListener('visitorConsentCollected', (event) => {
    evaluate();
  });

  if (window.Shopify && window.Shopify.customerPrivacy) evaluate();
  else if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) evaluate();
    });
  }
})();
